import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { CURRENCY_ORDER, type CurrencyCode,isCurrencyCode } from '@/shared/lib/currency';

import type { PreferencesState, QuickBuyPresets } from './types';

const PRESETS_KEY = 'pvp:quick-buy-presets';
const CURRENCY_KEY = 'pvp:selected-currency';
const PRESET_SLOTS = 3;

/** Sensible per-currency defaults shown until the user customizes them. */
const DEFAULT_PRESETS: QuickBuyPresets = {
  TON: [1, 5, 10],
  STARS: [50, 100, 500],
  GIFT: [1, 3, 5],
};

function sanitize(values: unknown): number[] | null {
  if (!Array.isArray(values)) return null;
  const nums = values
    .map((v) => Number(v))
    .filter((n) => Number.isFinite(n) && n > 0)
    .slice(0, PRESET_SLOTS)
    .sort((a, b) => a - b);
  return nums.length === PRESET_SLOTS ? nums : null;
}

function loadPresets(): QuickBuyPresets {
  const presets: QuickBuyPresets = { ...DEFAULT_PRESETS };
  try {
    const raw = localStorage.getItem(PRESETS_KEY);
    if (!raw) return presets;
    const parsed = JSON.parse(raw) as Partial<Record<CurrencyCode, unknown>>;
    for (const currency of CURRENCY_ORDER) {
      const valid = sanitize(parsed[currency]);
      if (valid) presets[currency] = valid;
    }
  } catch {
    /* ignore malformed/unavailable storage, fall back to defaults */
  }
  return presets;
}

function persistPresets(presets: QuickBuyPresets): void {
  try {
    localStorage.setItem(PRESETS_KEY, JSON.stringify(presets));
  } catch {
    /* storage may be unavailable (private mode); ignore */
  }
}

function loadCurrency(): CurrencyCode {
  try {
    const v = localStorage.getItem(CURRENCY_KEY);
    if (v && isCurrencyCode(v)) return v;
  } catch {
    /* storage may be unavailable; ignore */
  }
  return 'STARS';
}

const initialState: PreferencesState = {
  selectedCurrency: loadCurrency(),
  quickBuyPresets: loadPresets(),
};

const preferencesSlice = createSlice({
  name: 'preferences',
  initialState,
  reducers: {
    setSelectedCurrency(state, action: PayloadAction<CurrencyCode>) {
      state.selectedCurrency = action.payload;
      try {
        localStorage.setItem(CURRENCY_KEY, action.payload);
      } catch {
        /* storage may be unavailable; ignore */
      }
    },
    setQuickBuyPresets(
      state,
      action: PayloadAction<{ currency: CurrencyCode; values: number[] }>,
    ) {
      const { currency, values } = action.payload;
      const valid = sanitize(values);
      if (!valid) return;
      state.quickBuyPresets[currency] = valid;
      persistPresets(state.quickBuyPresets);
    },
    resetQuickBuyPresets(state, action: PayloadAction<CurrencyCode>) {
      state.quickBuyPresets[action.payload] = [...DEFAULT_PRESETS[action.payload]];
      persistPresets(state.quickBuyPresets);
    },
  },
});

export const preferencesActions = preferencesSlice.actions;
export const preferencesReducer = preferencesSlice.reducer;
export { DEFAULT_PRESETS,PRESET_SLOTS };
